import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lightbulb, Loader2, CheckCircle2, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getTopicsForShooting, type VideoTopic } from "@/services/data/videoTopics";

interface ShootingTopicsModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly shootingId: string;
  readonly shootingTitle?: string;
  readonly shootingDate?: Date;
}

export function ShootingTopicsModal({
  isOpen,
  onClose,
  shootingId,
  shootingTitle,
  shootingDate,
}: ShootingTopicsModalProps) {
  const navigate = useNavigate();
  const [topics, setTopics] = useState<VideoTopic[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const handleGoToTopics = () => {
    onClose();
    navigate('/app/user-topics');
  };

  useEffect(() => {
    if (isOpen && shootingId) {
      loadTopics();
    }
  }, [isOpen, shootingId]);

  const loadTopics = async () => {
    setIsLoading(true);
    const result = await getTopicsForShooting(shootingId);
    if (result.success && result.data) {
      setTopics(result.data);
    }
    setIsLoading(false);
  };

  const formattedDate = shootingDate
    ? shootingDate.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
      })
    : null;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
        </div>
      );
    }

    if (topics.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/5 mb-4">
            <Lightbulb className="h-8 w-8 text-white/30" />
          </div>
          <p className="text-white/60">Aucun sujet prévu</p>
          <p className="text-sm text-white/40 mt-1 mb-4">
            Ajoutez des idées de sujets pour ce tournage
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleGoToTopics}
            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter des sujets
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {topics.map((topic, index) => (
          <div
            key={topic.id}
            className="group rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4"
          >
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/20 flex-shrink-0">
                <span className="text-sm font-semibold text-emerald-400">
                  {index + 1}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-white">{topic.title}</h3>
                {topic.description && (
                  <p className="text-sm text-white/60 mt-1 line-clamp-2">
                    {topic.description}
                  </p>
                )}
              </div>
              <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-black/95 border border-white/10 text-white sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/20">
              <Lightbulb className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <DialogTitle className="text-lg">
                Sujets prévus
              </DialogTitle>
              {formattedDate && (
                <p className="text-sm text-white/50">
                  Tournage du {formattedDate}
                </p>
              )}
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] mt-4">
          {renderContent()}
        </ScrollArea>

        {topics.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
            <p className="text-sm text-white/40">
              {topics.length} sujet{topics.length > 1 ? "s" : ""} prévu{topics.length > 1 ? "s" : ""}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGoToTopics}
              className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
            >
              <Plus className="h-4 w-4 mr-1" />
              Ajouter
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
